import React, { useEffect } from 'react'
import {
	Route,
	Switch,
	useHistory,
	useLocation,
	withRouter,
} from 'react-router-dom'

import * as queryString from 'query-string'
import { ProtectedRoute } from './components/common/hoc'
import HomeScreen from './screens/home'
import EmailScreen from './screens/email'
import FeaturesScreen from './screens/features'
import ProcessingScreen from './screens/processing'
import QuestionScreen from './screens/question'
import SaleScreen from './screens/sale'
import SuccessScreen from './screens/success'
import { Media } from './components/common'
import ErrorScreen from './screens/error'
import Account from './screens/account'
import Unsubscribe from './screens/unsubscribe'
import Upsell from './screens/upsell'
import BagScreen from './screens/bag'
import {
	ampEventWithEventProperty,
	ampSetUserProperty,
} from './utils/amplitude'
import Discount from './screens/discount/component'
import * as cookie from 'cookie'
import { useDispatch, useSelector } from 'react-redux'
import {
	getUserId,
	setFbc,
	setFbp,
	setUserId,
	setFlowType,
	setFlowTag,
	setIsLegalFlow,
	getIsRedirectingToLegal,
	setIsRedirectingToLegal,
	setIsOnlyLegalFlow,
	getCountryCode,
	setCountryCode,
} from './redux/slices/userSlice'
import { paramsToQuery } from './utils/historyDecorators'
import { setCartoonifyFlow, startQuiz } from './redux/slices/questionsSlice'
import PDFViewer from './components/common/PDFViewer'
import { getValueByKey } from './utils/remoteConfigs'
import { setInitialPrice, setUpsellPrice } from './redux/slices/configSlice'
import { onlyLegalFlowCountry } from './utils/onlyLegalFlowCountry'

const App = () => {
	const { pathname } = useLocation()
	const history = useHistory()
	const dispatch = useDispatch()
	const user_id = useSelector(getUserId)
	const isRedirectingToLegal = useSelector(getIsRedirectingToLegal)
	const countryCode = useSelector(getCountryCode)
	const params = queryString.parse(location.search)

	useEffect(() => {
		ampSetUserProperty('user_id', user_id || params.user_id || window.id)
	}, [])

	useEffect(() => {
		if (pathname !== '/error') {
			setTimeout(() => {
				ampEventWithEventProperty('firstpageview', { location: location.href })
			}, 0)
		}
	}, [])
	useEffect(() => {
		setTimeout(() => {
			const cookies = cookie.parse(document.cookie)
			dispatch(setFbc({ fbc: cookies._fbc || '' }))
			dispatch(setFbp({ fbp: cookies._fbp || '' }))
		}, 2000)
	})
	useEffect(() => {
		ampSetUserProperty(
			'origin',
			window.location.origin,
			user_id || params.user_id || window.id,
		)
	}, [])
	useEffect(() => {
		window.scrollTo(0, 0)

		if (isRedirectingToLegal) {
			dispatch(setIsRedirectingToLegal({ isRedirectingToLegal: false }))
			return
		}

		Object.keys(params).forEach(elem => {
			if (elem === 'user_id') {
				dispatch(setUserId({ user_id: params[elem] }))
				ampSetUserProperty(
					elem,
					params[elem],
					user_id || params.user_id || window.id,
				)
			} else if (elem === 'is_confirmation_email') {
				ampEventWithEventProperty('is_confirmation_email', { param: true })
			} else {
				ampSetUserProperty(
					elem,
					params[elem],
					user_id || params.user_id || window.id,
				)
			}
		})
	}, [location.href])

	useEffect(() => {
		window.history.scrollRestoration = 'manual'

		if (pathname === '/' && params.flow?.includes('-3')) {
			dispatch(setIsLegalFlow({ isLegalFlow: true }))
		} else if (pathname === '/' && params.flow) {
			dispatch(setIsLegalFlow({ isLegalFlow: false }))
		}

		if (params.flow?.includes('22')) {
			dispatch(setFlowTag({ flow_tag: '22' }))
		} else if (params.flow?.includes('33')) {
			dispatch(setFlowTag({ flow_tag: '33' }))
			dispatch(setFlowType({ flow_type: '6' }))
			dispatch(setCartoonifyFlow())
		} else if (params.flow?.includes('55')) {
			dispatch(setFlowTag({ flow_tag: '55' }))
			dispatch(setFlowType({ flow_type: '10' }))
		} else if ((!params.flow && pathname === '/') || params.flow === '11') {
			if (params.flow === '11') {
				dispatch(setFlowTag({ flow_tag: params.flow }))
			} else {
				dispatch(setFlowTag({ flow_tag: '' }))
				ampSetUserProperty(
					'flow',
					'default',
					user_id || params.user_id || window.id,
				)
				dispatch(setIsLegalFlow({ isLegalFlow: true }))
			}
			dispatch(setFlowType({ flow_type: '2' }))
			dispatch(startQuiz())
		}

		if (!params.user_id) {
			history.push({
				pathname: location.pathname,
				search: paramsToQuery({
					user_id: user_id || window.id,
					external_id: user_id || window.id,
					...params,
				}),
			})
			dispatch(setUserId({ user_id: user_id || window.id }))
		} else {
			dispatch(setUserId({ user_id: params.user_id }))
		}
	}, [])

	useEffect(() => {
		setTimeout(() => {
			if (user_id) {
				if (countryCode) {
					ampSetUserProperty('country', countryCode, user_id)
				}
				ampEventWithEventProperty('pageview', { location: location.href })
			}
		}, 0)
	}, [location.href, user_id])

	useEffect(() => {
		if (!countryCode) {
			try {
				fetch(
					'https://ipgeolocation.abstractapi.com/v1/?api_key=1c4acaa10922477bb9fb47f82a780a0b',
				)
					.then(res => res.json())
					.then(res => {
						dispatch(setCountryCode({ countryCode: res.country_code }))
					})
			} catch (e) {
				console.error(e)
			}
		}
	}, [])

	useEffect(() => {
		if (onlyLegalFlowCountry.includes(countryCode)) {
			dispatch(setIsOnlyLegalFlow({ isOnlyLegalFlow: true }))
			dispatch(setIsLegalFlow({ isLegalFlow: true }))
			if (params?.flow === '11') {
				dispatch(setFlowTag({ flow_tag: '' }))
				dispatch(setFlowType({ flow_type: '2' }))
			}
		}

		if (
			countryCode &&
			!onlyLegalFlowCountry.includes(countryCode) &&
			params.flow &&
			!params.flow.includes('-3')
		) {
			if (!params.upsell_test) {
				getValueByKey('upsell_price').then(res => {
					console.log('upsell_price', res)
					const value = parseInt(res)
					ampSetUserProperty(
						'upsell_test',
						value,
						user_id || params.user_id || window.id,
					)
					dispatch(setUpsellPrice({ upsell_price: value }))
				})
			} else {
				const value = parseInt(params.upsell_test)
				dispatch(setUpsellPrice({ upsell_price: value }))
			}

			if (!params.initial_price) {
				getValueByKey('initial_price').then(res => {
					console.log('initial_price', res)
					ampSetUserProperty(
						'initial_price',
						res,
						user_id || params.user_id || window.id,
					)
					dispatch(setInitialPrice({ initial_price: res }))
				})
			} else {
				dispatch(setInitialPrice({ initial_price: params.initial_price }))
			}
		}
	}, [countryCode])

	return (
		<>
			<Switch>
				<Route path="/" component={() => <HomeScreen />} exact />
				<Route path="/question/:id" component={QuestionScreen} exact />
				<Route path="/features" component={FeaturesScreen} />
				<Route path="/processing" component={ProcessingScreen} />
				<Route path="/email" component={EmailScreen} />
				<Route path="/sale-1" component={SaleScreen} exact />
				<Route path="/sale-2" component={SaleScreen} exact />
				<Route path="/sale11-1" component={SaleScreen} exact />
				<Route path="/sale11-2" component={SaleScreen} exact />
				<Route path="/sale22-1" component={SaleScreen} exact />
				<Route path="/sale22-2" component={SaleScreen} exact />
				<Route path="/sale22-3" component={SaleScreen} exact />
				<Route path="/sale22-4" component={SaleScreen} exact />
				<Route path="/sale33-1" component={SaleScreen} exact />
				<Route path="/sale33-2" component={SaleScreen} exact />
				<Route path="/sale33-3" component={SaleScreen} exact />
				<Route path="/sale33-4" component={SaleScreen} exact />
				<Route path="/sale55-1" component={SaleScreen} exact />
				<Route path="/sale55-2" component={SaleScreen} exact />
				<Route path="/sale55-3" component={SaleScreen} exact />
				<Route path="/sale55-4" component={SaleScreen} exact />
				<Route path="/discount" component={Discount} exact />
				<Route path="/discount11" component={Discount} exact />
				<Route path="/account" component={Account} exact />
				<Route path="/unsubscribe/:stage" component={Unsubscribe} exact />
				<Route path="/upsell1" bill={true} component={Upsell} />
				<Route path="/upsell2" bill={true} component={Upsell} />
				<Route path="/upsell3" bill={true} component={Upsell} />
				<Route path="/upsell4" bill={true} component={Upsell} />
				<Route path="/bag" component={BagScreen} />
				<Route
					path="/privacy-policy"
					component={() => <PDFViewer url="/pdf/Privacy Policy.pdf" />}
				/>
				<Route
					path="/terms-of-use"
					component={() => <PDFViewer url="/pdf/Terms of Use.pdf" />}
				/>
				<Route
					path="/subscription-terms"
					component={() => (
						<PDFViewer url="/pdf/General Subscription Terms.pdf" />
					)}
				/>
				<Route
					path="/money-back-policy"
					component={() => <PDFViewer url="/pdf/Money-back Policy.pdf" />}
				/>
				<Route path="/faq" component={() => <PDFViewer url="/pdf/FAQ.pdf" />} />
				<Route path="/success" bill={true} component={SuccessScreen} />
				<ProtectedRoute path="/error" bill={false} component={ErrorScreen} />
			</Switch>
			<Media />
		</>
	)
}

export default withRouter(App)
